import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import vvtfImage from '../../vvtf.webp';
import { pickHomeHeroAuraStep } from '../../utils/auraColor';

/** Matches the hero cloud's color cadence (ROTATION_INTERVAL_MS) so the mini aura flips in the same rhythm. */
const MINI_AURA_CYCLE_MS = 1500;

const AURA_CSS_VAR_KEYS = [
  '--aura-rgb-light',
  '--aura-rgb',
  '--aura-rgb-dark',
  '--aura-rgb-darker',
  '--aura-rgb-darkest',
  '--aura-digit-highlight',
  '--aura-glow-soft',
  '--aura-glow-mid',
  '--aura-glow-strong',
  '--aura-glow-digit',
  '--aura-icon-glow-1',
  '--aura-icon-glow-2',
];

function applyAuraStyleVars(element, vars) {
  if (!element) return;
  if (!vars) {
    AURA_CSS_VAR_KEYS.forEach((key) => element.style.removeProperty(key));
    return;
  }
  Object.entries(vars).forEach(([key, value]) => {
    element.style.setProperty(key, value);
  });
}

export default function VisualVibeTransformerExplainer({
  open,
  onClose,
  signedIn = true,
  onSignUp,
}) {
  const auraRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const el = auraRef.current;
    if (!el) return undefined;

    let prevKey = null;
    const applyStep = () => {
      const step = pickHomeHeroAuraStep(prevKey);
      prevKey = step.key;
      applyAuraStyleVars(el, step.vars);
    };

    applyStep();

    if (typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const id = window.setInterval(applyStep, MINI_AURA_CYCLE_MS);
    return () => window.clearInterval(id);
  }, [open]);

  if (!open) return null;

  const handlePrimaryAction = () => {
    onClose();
    if (!signedIn && typeof onSignUp === 'function') onSignUp();
  };

  return createPortal(
    <div
      className="cart-popup-overlay vibe-popup-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="cart-popup vibe-popup visual-vibe-transformer-explainer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="visual-vibe-transformer-explainer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="cart-popup-close"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>
        <h3
          id="visual-vibe-transformer-explainer-title"
          className="match-explainer-title"
        >
          Visual-Vibe transformer
        </h3>
        <div className="visual-vibe-transformer-explainer-graphic-wrap">
          <img
            src={vvtfImage}
            alt="Visual vibe transformer diagram"
            className="visual-vibe-transformer-explainer-graphic"
          />
        </div>
        <p className="vibe-popup-text visual-vibe-transformer-explainer-tagline">
          LLMs predict words.
          <br />
          VVTF predicts vibes.
        </p>
        <p className="vibe-popup-text">
          Rather than using an LLM, we developed our own Visual-Vibe Transformer that translates visuals into emotional coordinates to predict a match.
        </p>
        <p className="vibe-popup-text">
          Because up to 90% of human communication is non-verbal.
        </p>
        <p className="vibe-popup-text">
          The visuals you pick predict the unique color of{' '}
          <span className="vvtf-mini-aura" ref={auraRef}>
            <span className="vvtf-mini-aura__blob vvtf-mini-aura__blob--one" />
            <span className="vvtf-mini-aura__blob vvtf-mini-aura__blob--two" />
            <span className="vvtf-mini-aura__blob vvtf-mini-aura__blob--three" />
            <span className="vvtf-mini-aura__text">your vibe cloud</span>
          </span>
          .
        </p>
        <div className="visual-vibe-transformer-explainer-actions">
          <button type="button" className="vibe-popup-btn" onClick={handlePrimaryAction}>
            {signedIn ? 'GOT IT' : 'SIGN UP'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
